/**
 * Browser-Safe Analytics Events
 * 
 * Pure client-side event constants that don't require Node.js dependencies
 * Isolated from server-side configuration logic
 */

// Lead Generation Events with Scoring (1-100 scale)
export const LEAD_GENERATION_EVENTS = {
	// Low-intent actions (5-25 points)
	LEAD_PAGE_VIEW: 'lead_page_view',
	LEAD_CONTENT_VIEW: 'lead_content_view',
	LEAD_PRODUCT_VIEW: 'lead_product_view',
	LEAD_GALLERY_VIEW: 'lead_gallery_view',
	LEAD_SCROLL: 'lead_scroll',
	LEAD_SCROLL_ENGAGED: 'lead_scroll_engaged',

	// Medium-intent actions (30-50 points)
	LEAD_ORDER_STEP_1_DISPLAYED: 'lead_order_step_1_displayed',
	LEAD_PRODUCT_INTEREST: 'lead_product_interest',
	LEAD_INQUIRY_STARTED: 'lead_inquiry_started',
	LEAD_CALCULATOR_USED: 'lead_calculator_used',
	LEAD_DOWNLOAD_RESOURCE: 'lead_download_resource',
	LEAD_QUALIFICATION: 'lead_qualification',

	// High-intent actions (60-85 points)
	LEAD_ORDER_STEP_2_DISPLAYED: 'lead_order_step_2_displayed',
	LEAD_ORDER_STEP_3_DISPLAYED: 'lead_order_step_3_displayed',
	LEAD_ORDER_STEP_4_DISPLAYED: 'lead_order_step_4_displayed',
	LEAD_CONTACT_INFO: 'lead_contact_info',
	LEAD_DEMO_REQUESTED: 'lead_demo_requested',
	LEAD_QUOTE_REQUESTED: 'lead_quote_requested',
	LEAD_WHATSAPP_CONTACT: 'lead_whatsapp_contact',

	// Conversion actions (90-100 points)
	LEAD_PHONE_CALL: 'lead_phone_call',
	LEAD_MEETING_SCHEDULED: 'lead_meeting_scheduled',
	LEAD_FORM_SUBMITTED: 'lead_form_submitted',
} as const;

// Lead Scoring Values
export const LEAD_SCORES = {
	// Low-intent (5-25)
	[LEAD_GENERATION_EVENTS.LEAD_PAGE_VIEW]: 5,
	[LEAD_GENERATION_EVENTS.LEAD_CONTENT_VIEW]: 15,
	[LEAD_GENERATION_EVENTS.LEAD_PRODUCT_VIEW]: 20,
	[LEAD_GENERATION_EVENTS.LEAD_GALLERY_VIEW]: 20,
	[LEAD_GENERATION_EVENTS.LEAD_SCROLL]: 25,
	[LEAD_GENERATION_EVENTS.LEAD_SCROLL_ENGAGED]: 25,

	// Medium-intent (30-50)
	[LEAD_GENERATION_EVENTS.LEAD_ORDER_STEP_1_DISPLAYED]: 30,
	[LEAD_GENERATION_EVENTS.LEAD_PRODUCT_INTEREST]: 30,
	[LEAD_GENERATION_EVENTS.LEAD_INQUIRY_STARTED]: 40,
	[LEAD_GENERATION_EVENTS.LEAD_CALCULATOR_USED]: 45,
	[LEAD_GENERATION_EVENTS.LEAD_DOWNLOAD_RESOURCE]: 50,
	[LEAD_GENERATION_EVENTS.LEAD_QUALIFICATION]: 50,

	// High-intent (60-85)
	[LEAD_GENERATION_EVENTS.LEAD_ORDER_STEP_2_DISPLAYED]: 60,
	[LEAD_GENERATION_EVENTS.LEAD_ORDER_STEP_3_DISPLAYED]: 65,
	[LEAD_GENERATION_EVENTS.LEAD_ORDER_STEP_4_DISPLAYED]: 70,
	[LEAD_GENERATION_EVENTS.LEAD_CONTACT_INFO]: 60,
	[LEAD_GENERATION_EVENTS.LEAD_DEMO_REQUESTED]: 70,
	[LEAD_GENERATION_EVENTS.LEAD_QUOTE_REQUESTED]: 80,
	[LEAD_GENERATION_EVENTS.LEAD_WHATSAPP_CONTACT]: 85,

	// Conversion (90-100)
	[LEAD_GENERATION_EVENTS.LEAD_PHONE_CALL]: 90,
	[LEAD_GENERATION_EVENTS.LEAD_MEETING_SCHEDULED]: 95,
	[LEAD_GENERATION_EVENTS.LEAD_FORM_SUBMITTED]: 100,
} as const;

// E-commerce Events (GA4 Standard)
export const ECOMMERCE_EVENTS = {
	// Discovery
	VIEW_ITEM: 'view_item',
	VIEW_ITEM_LIST: 'view_item_list',
	SELECT_ITEM: 'select_item',
	VIEW_PROMOTION: 'view_promotion',
	SELECT_PROMOTION: 'select_promotion',

	// Shopping behavior
	ADD_TO_CART: 'add_to_cart',
	REMOVE_FROM_CART: 'remove_from_cart',
	VIEW_CART: 'view_cart',
	ADD_TO_WISHLIST: 'add_to_wishlist',

	// Checkout process
	BEGIN_CHECKOUT: 'begin_checkout',
	ADD_PAYMENT_INFO: 'add_payment_info',
	ADD_SHIPPING_INFO: 'add_shipping_info',

	// Transaction
	PURCHASE: 'purchase',
	REFUND: 'refund',
} as const;

// Export types
export type LeadGenerationEvent =
	(typeof LEAD_GENERATION_EVENTS)[keyof typeof LEAD_GENERATION_EVENTS];
export type EcommerceEvent = (typeof ECOMMERCE_EVENTS)[keyof typeof ECOMMERCE_EVENTS];
export type StandardizedEvent = LeadGenerationEvent | EcommerceEvent;

// Combined events for convenience
export const ALL_EVENTS = {
	...LEAD_GENERATION_EVENTS,
	...ECOMMERCE_EVENTS,
} as const;